import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Checkbox,
  OutlinedInput,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Campaign as CampaignIcon,
  RadioButtonChecked as BreakIcon,
  ViewList,
  ViewModule,
  LibraryMusic,
  PlayArrow,
  Visibility,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import FranchiseBreakPanel from '../components/FranchiseBreakPanel';
import CommercialBreaksPanel from '../components/CommercialBreaksPanel';
import { StatusChip, segmentedToggleSx } from '../components/advertisingUi';
import AutoGenerationSchedules from '../components/AutoGenerationSchedules';
import ExternalAdvertisingSettings from '../components/ExternalAdvertisingSettings';
import CampaignWizard from '../components/CampaignWizard';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { io as socketIo } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const parseLocalCalendarDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return value ? new Date(value) : null;
};

// Deterministic accent hue per campaign so each card gets a distinct monogram tile
const AVATAR_HUES = [188, 210, 245, 275, 315, 155, 25];
const getCampaignHue = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_HUES[hash % AVATAR_HUES.length];
};
const getCampaignInitials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const CampaignAvatar = ({ name, size = 44 }) => {
  const hue = getCampaignHue(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: `${Math.round(size * 0.28)}px`,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: size * 0.34,
        letterSpacing: '0.02em',
        color: `hsl(${hue}, 90%, 78%)`,
        background: `linear-gradient(135deg, hsla(${hue}, 85%, 60%, 0.28) 0%, hsla(${hue + 40}, 85%, 55%, 0.12) 100%)`,
        border: `1px solid hsla(${hue}, 85%, 65%, 0.35)`,
        textShadow: `0 0 14px hsla(${hue}, 90%, 65%, 0.6)`,
      }}
    >
      {getCampaignInitials(name)}
    </Box>
  );
};

export default function Advertising() {
  const { getModulePermission, isAdmin, user } = useAuth();
  const canEditAdvertising = getModulePermission('advertising')?.can_edit === true;
  const canManageExternalAdvertising = typeof isAdmin === 'function'
    ? isAdmin()
    : (user?.role === 'admin' || user?.role === 'superadmin');
  const [tabValue, setTabValue] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStationFilter, setCampaignStationFilter] = useState(''); // optional filter by station
  const [campaignSection, setCampaignSection] = useState('active'); // 'active' | 'archive'
  const [breaks, setBreaks] = useState([]);
  const [stations, setStations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [franchiseTargets, setFranchiseTargets] = useState([]);
  const [campaignViewMode, setCampaignViewMode] = useState('card'); // 'card' | 'list'
  const [campaignSort, setCampaignSort] = useState({ orderBy: 'name', order: 'asc' });
  const [trafficLogSort, setTrafficLogSort] = useState({ orderBy: 'scheduled_time', order: 'desc' });
  const [daypartSort, setDaypartSort] = useState({ orderBy: 'name', order: 'asc' });
  const [businessCatSort, setBusinessCatSort] = useState({ orderBy: 'name', order: 'asc' });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  // Unified campaign wizard (details → spots → schedule/segments)
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardCampaign, setWizardCampaign] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  // AdMaster: segments, business categories, traffic log
  const [businessCategories, setBusinessCategories] = useState([]);
  const [segmentTemplates, setSegmentTemplates] = useState([]);
  // Traffic log
  const [trafficLog, setTrafficLog] = useState([]);
  const [trafficLogFilters, setTrafficLogFilters] = useState({ station_id: '', date: formatLocalDate(), date_end: '', campaign_id: '', break_id: '', region_id: '' });
  const [externalAdStationId, setExternalAdStationId] = useState('');
  const [trafficLogBreaks, setTrafficLogBreaks] = useState([]);
  const [trafficSelectedHour, setTrafficSelectedHour] = useState(new Date().getHours());
  const [trafficSelectedBreakItemId, setTrafficSelectedBreakItemId] = useState('');
  const [trafficDragScheduledId, setTrafficDragScheduledId] = useState(null);
  const [trafficManualAdd, setTrafficManualAdd] = useState({ campaign_id: '', spot_id: '' });
  const [trafficManualSpots, setTrafficManualSpots] = useState([]);
  const [selectedScheduledAd, setSelectedScheduledAd] = useState(null);
  const [openAddScheduledDialog, setOpenAddScheduledDialog] = useState(false);
  const [scheduledAdForm, setScheduledAdForm] = useState({ station_id: '', campaign_id: '', spot_id: '', scheduled_time: '', position_in_break: 0 });
  const [scheduledAdSpots, setScheduledAdSpots] = useState([]);
  const [openAddToAllBreaksDialog, setOpenAddToAllBreaksDialog] = useState(false);
  const [addToAllBreaksForm, setAddToAllBreaksForm] = useState({ station_id: '', campaign_id: '', spot_id: '', date: '' });
  const [addToAllBreaksSpots, setAddToAllBreaksSpots] = useState([]);
  const [openGenerateScheduleDialog, setOpenGenerateScheduleDialog] = useState(false);
  const [generateScheduleForm, setGenerateScheduleForm] = useState({ station_id: '', date_from: formatLocalDate(), date_to: formatLocalDate() });
  const [scheduleGenerating, setScheduleGenerating] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState({ percent: 0, message: '' });
  const scheduleGenerationPendingRef = useRef(false);
  const socketRef = useRef(null);
  const fetchTrafficLogRef = useRef(null);
  const [dayparts, setDayparts] = useState([]);
  // Advertising Settings (Day Parts, Business Categories)
  const [daypartForm, setDaypartForm] = useState({ name: '', start_time: '06:00', end_time: '10:00', station_id: '' });
  const [daypartDialogOpen, setDaypartDialogOpen] = useState(false);
  const [editingDaypart, setEditingDaypart] = useState(null);
  const [businessCatForm, setBusinessCatForm] = useState({ name: '', description: '' });
  const [businessCatDialogOpen, setBusinessCatDialogOpen] = useState(false);
  const [editingBusinessCat, setEditingBusinessCat] = useState(null);
  // Advertising Reports
  const [reportType, setReportType] = useState('scheduled'); // 'scheduled' | 'played'
  const [reportFilters, setReportFilters] = useState({
    station_id: '',
    client: '',
    campaign_id: '',
    date_from: formatLocalDate(),
    date_to: formatLocalDate(),
  });
  const [reportData, setReportData] = useState({ range: null, rows: [], count: 0 });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportClients, setReportClients] = useState([]);
  const [reportError, setReportError] = useState(null);
  const [bulkBreakToast, setBulkBreakToast] = useState({ open: false, severity: 'success', message: '' });

  const showToast = (message, severity = 'info') => {
    setBulkBreakToast({
      open: true,
      severity,
      message: String(message || ''),
    });
  };

  const alert = (message) => {
    const text = String(message || '');
    const lower = text.toLowerCase();
    const severity = /failed|error|invalid|required|must|not found|unable|cannot|can't/.test(lower)
      ? 'error'
      : /success|complete|completed|queued|created|updated|deleted|saved|added/.test(lower)
        ? 'success'
        : 'info';
    showToast(text, severity);
  };

  // Socket.io connection for generation progress events
  useEffect(() => {
    const socket = socketIo(API_URL);
    socketRef.current = socket;

    socket.on('generation:progress', (data) => {
      if (data.type === 'traffic') {
        setScheduleProgress({ percent: data.percent || 0, message: data.message || '' });
        if (data.done && scheduleGenerationPendingRef.current) {
          scheduleGenerationPendingRef.current = false;
          setScheduleGenerating(false);
          if (data.error) {
            alert('Failed: ' + (data.message || 'Generation failed'));
            return;
          }
          if (fetchTrafficLogRef.current) fetchTrafficLogRef.current();
          setOpenGenerateScheduleDialog(false);
          alert(data.message || 'Schedule generation complete');
        }
      }
    });

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    fetchBreaks();
    fetchStations();
    fetchRegions();
    fetchBusinessCategories();
    fetchSegmentTemplates();
    fetchDayparts();
    fetchFranchiseTargets();
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [campaignStationFilter]);

  useEffect(() => {
    if (!externalAdStationId && stations.length) {
      setExternalAdStationId(campaignStationFilter || stations[0].id);
    }
  }, [stations, campaignStationFilter, externalAdStationId]);

  const fetchDayparts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/dayparts`, { withCredentials: true });
      setDayparts(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchTrafficLog = async () => {
    const params = new URLSearchParams();
    if (trafficLogFilters.station_id) params.append('station_id', trafficLogFilters.station_id);
    if (trafficLogFilters.date_end && trafficLogFilters.date) {
      params.append('date_from', trafficLogFilters.date);
      params.append('date_to', trafficLogFilters.date_end);
    } else if (trafficLogFilters.date) {
      params.append('date', trafficLogFilters.date);
    }
    if (trafficLogFilters.campaign_id) params.append('campaign_id', trafficLogFilters.campaign_id);
    if (trafficLogFilters.break_id) params.append('break_id', trafficLogFilters.break_id);
    if (trafficLogFilters.region_id) params.append('region_id', trafficLogFilters.region_id);
    try {
      const res = await axios.get(`${API_URL}/api/advertising/traffic-log?${params}`, { withCredentials: true });
      setTrafficLog(res.data || []);
    } catch { setTrafficLog([]); }
  };

  // Keep ref in sync so the socket handler always calls the latest version
  useEffect(() => {
    fetchTrafficLogRef.current = fetchTrafficLog;
  });

  useEffect(() => {
    if (tabValue === 2) fetchTrafficLog();
    if (tabValue === 3) fetchReportClients();
    if (tabValue === 4) { fetchDayparts(); fetchBusinessCategories(); }
  }, [tabValue, trafficLogFilters.station_id, trafficLogFilters.date, trafficLogFilters.date_end, trafficLogFilters.campaign_id, trafficLogFilters.break_id, trafficLogFilters.region_id]);

  useEffect(() => {
    if (tabValue === 2 && trafficLogFilters.station_id) {
      axios.get(`${API_URL}/api/advertising/breaks?station_id=${trafficLogFilters.station_id}`, { withCredentials: true })
        .then((res) => setTrafficLogBreaks(res.data || []))
        .catch(() => setTrafficLogBreaks([]));
    } else {
      setTrafficLogBreaks([]);
    }
  }, [tabValue, trafficLogFilters.station_id]);

  useEffect(() => {
    if (!trafficLogFilters.break_id) {
      setTrafficSelectedBreakItemId('');
      return;
    }
    const selectedDate = trafficLogFilters.date || formatLocalDate();
    const dayOfWeek = new Date(`${selectedDate}T00:00:00`).getDay();
    const stillValid = trafficLogBreaks
      .filter((b) => Number(b.hour) === Number(trafficSelectedHour))
      .filter((b) => b.day_of_week === null || b.day_of_week === undefined || Number(b.day_of_week) === dayOfWeek)
      .some((b) => String(b.id) === String(trafficLogFilters.break_id));
    if (!stillValid) {
      setTrafficLogFilters((prev) => ({ ...prev, break_id: '' }));
      setTrafficSelectedBreakItemId('');
    }
  }, [trafficSelectedHour, trafficLogFilters.date, trafficLogBreaks, trafficLogFilters.break_id]);

  useEffect(() => {
    if (!trafficManualAdd.campaign_id) {
      setTrafficManualSpots([]);
      setTrafficManualAdd((prev) => ({ ...prev, spot_id: '' }));
      return;
    }
    axios.get(`${API_URL}/api/advertising/campaigns/${trafficManualAdd.campaign_id}/spots`, { withCredentials: true })
      .then((res) => setTrafficManualSpots(res.data || []))
      .catch(() => setTrafficManualSpots([]));
  }, [trafficManualAdd.campaign_id]);

  const fetchReport = async () => {
    try {
      setReportLoading(true);
      setReportError(null);
      const params = new URLSearchParams();
      if (reportFilters.station_id) params.append('station_id', reportFilters.station_id);
      if (reportFilters.client) params.append('client', reportFilters.client);
      if (reportFilters.campaign_id) params.append('campaign_id', reportFilters.campaign_id);
      if (reportFilters.date_from) params.append('date_from', reportFilters.date_from);
      if (reportFilters.date_to) params.append('date_to', reportFilters.date_to);
      const url = `${API_URL}/api/advertising/reports/${reportType}?${params.toString()}`;
      const res = await axios.get(url, { withCredentials: true });
      setReportData(res.data || { range: null, rows: [], count: 0 });
    } catch (e) {
      console.error('Error fetching report:', e);
      setReportError(e.response?.data?.message || e.message || 'Failed to generate report');
      setReportData({ range: null, rows: [], count: 0 });
    } finally {
      setReportLoading(false);
    }
  };

  const fetchReportClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/reports/clients`, { withCredentials: true });
      setReportClients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching report clients:', e);
      setReportClients([]);
    }
  };

  const selectedTrafficDate = trafficLogFilters.date || formatLocalDate();
  const selectedTrafficDayOfWeek = new Date(`${selectedTrafficDate}T00:00:00`).getDay();
  // Breaks always come from the main station (region_id IS NULL) so break length is from the master
  const breaksForSelectedHour = [...trafficLogBreaks]
    .filter((b) => Number(b.hour) === Number(trafficSelectedHour))
    .filter((b) => b.day_of_week === null || b.day_of_week === undefined || Number(b.day_of_week) === selectedTrafficDayOfWeek)
    .filter((b) => !b.region_id)
    .sort((a, b) => (Number(a.minute) || 0) - (Number(b.minute) || 0));
  const selectedTrafficBreak = breaksForSelectedHour.find((b) => String(b.id) === String(trafficLogFilters.break_id));
  const regionsForSelectedStation = regions.filter((r) => String(r.station_id) === String(trafficLogFilters.station_id));
  const trafficBreakItems = [...trafficLog]
    .filter((row) => String(row.break_id) === String(trafficLogFilters.break_id))
    .filter((row) => row.scheduled_time && new Date(row.scheduled_time).getHours() === Number(trafficSelectedHour))
    .sort((a, b) => (a.position_in_break ?? 0) - (b.position_in_break ?? 0));
  const trafficRowsForView = [...trafficLog]
    .filter((row) => row.scheduled_time && new Date(row.scheduled_time).getHours() === Number(trafficSelectedHour))
    .filter((row) => !trafficLogFilters.break_id || String(row.break_id) === String(trafficLogFilters.break_id));

  // Split campaigns into active (current/scheduled) vs archived (finished / no longer scheduled)
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const activeCampaigns = campaigns.filter((c) => {
    const endDate = parseLocalCalendarDate(c.end_date);
    if (!endDate) return true;
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return endDay >= today;
  });
  const archivedCampaigns = campaigns.filter((c) => {
    const endDate = parseLocalCalendarDate(c.end_date);
    if (!endDate) return false;
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return endDay < today;
  });

  const handleManualAddSpotToSelectedBreak = async () => {
    if (!trafficLogFilters.station_id || !trafficLogFilters.break_id || !trafficManualAdd.campaign_id || !trafficManualAdd.spot_id) {
      alert('Select station, break, campaign and spot first.');
      return;
    }
    if (!selectedTrafficBreak) {
      alert('Select a valid break for the chosen day/hour.');
      return;
    }
    const hh = String(selectedTrafficBreak.hour ?? trafficSelectedHour).padStart(2, '0');
    const mm = String(selectedTrafficBreak.minute ?? 0).padStart(2, '0');
    const ss = '00';
    const scheduled_time = `${selectedTrafficDate}T${hh}:${mm}:${ss}`;
    try {
      const regionId = trafficLogFilters.region_id && trafficLogFilters.region_id !== 'master' ? parseInt(trafficLogFilters.region_id) : null;
      await axios.post(`${API_URL}/api/advertising/traffic-log/bulk`, {
        operations: [{
          action: 'create',
          data: {
            station_id: trafficLogFilters.station_id,
            break_id: trafficLogFilters.break_id,
            campaign_id: trafficManualAdd.campaign_id,
            spot_id: trafficManualAdd.spot_id,
            scheduled_time,
            position_in_break: trafficBreakItems.length,
            region_id: regionId,
          },
        }],
      }, { withCredentials: true });
      await fetchTrafficLog();
      setTrafficSelectedBreakItemId('');
      alert('Spot added to selected break.');
    } catch (e) {
      alert(`Failed to add spot: ${e.response?.data?.message || e.message}`);
    }
  };

  const handleTrafficDragStart = (id) => {
    if (!trafficLogFilters.break_id) return;
    setTrafficDragScheduledId(id);
  };

  const handleTrafficDrop = async (targetId) => {
    if (!trafficLogFilters.break_id || !trafficDragScheduledId || trafficDragScheduledId === targetId) {
      setTrafficDragScheduledId(null);
      return;
    }
    const rows = [...trafficBreakItems].sort(
      (a, b) => (a.position_in_break ?? 0) - (b.position_in_break ?? 0)
    );
    const fromIndex = rows.findIndex((r) => String(r.id) === String(trafficDragScheduledId));
    const toIndex = rows.findIndex((r) => String(r.id) === String(targetId));
    if (fromIndex === -1 || toIndex === -1) {
      setTrafficDragScheduledId(null);
      return;
    }
    const reordered = [...rows];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updates = [];
    reordered.forEach((row, index) => {
      if ((row.position_in_break ?? 0) !== index) {
        updates.push({ id: row.id, position_in_break: index });
      }
    });

    try {
      if (updates.length > 0) {
        await axios.post(
          `${API_URL}/api/advertising/traffic-log/bulk`,
          {
            operations: updates.map((u) => ({
              action: 'update',
              id: u.id,
              data: { position_in_break: u.position_in_break },
            })),
          },
          { withCredentials: true }
        );
      }
      await fetchTrafficLog();
    } catch (e) {
      alert(`Failed to reorder spots: ${e.response?.data?.message || e.message}`);
    } finally {
      setTrafficDragScheduledId(null);
    }
  };

  const reportCampaignOptions = campaigns.filter((c) => {
    if (!reportFilters.client) return true;
    return (c.client || '').trim() === reportFilters.client;
  });

  const fetchBusinessCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/business-categories`, { withCredentials: true });
      setBusinessCategories(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchSegmentTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/advertising/segment-templates`, { withCredentials: true });
      setSegmentTemplates(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchCampaigns = async () => {
    try {
      const params = campaignStationFilter ? { params: { station_id: campaignStationFilter } } : {};
      const response = await axios.get(`${API_URL}/api/advertising/campaigns`, {
        ...params,
        withCredentials: true
      });
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchBreaks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/advertising/breaks`, {
        withCredentials: true
      });
      setBreaks(response.data || []);
    } catch (error) {
      console.error('Error fetching breaks:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      setStations(response.data || []);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/regions`, {
        withCredentials: true
      });
      setRegions(response.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchFranchiseTargets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/syndication/advertising-targets`, {
        withCredentials: true,
      });
      setFranchiseTargets(response.data?.links || []);
    } catch (error) {
      if (![403, 404].includes(error.response?.status)) {
        console.error('Error fetching franchise advertising targets:', error);
      }
      setFranchiseTargets([]);
    }
  };

  // Opens the unified campaign wizard. step: 0 = details, 1 = spots, 2 = schedule (segments)
  const handleOpenWizard = (campaign = null, step = 0) => {
    if (campaign?.is_syndicated && step === 0) {
      alert('This mandatory franchise campaign is managed by the parent tenant and is read-only.');
      return;
    }
    setWizardCampaign(campaign);
    setWizardStep(campaign ? step : 0);
    setWizardOpen(true);
    fetchBusinessCategories();
  };

  // Only commercial breaks use this dialog; campaigns use the CampaignWizard
  const handleOpenDialog = (item = null) => {
    setEditingItem(item);

    {
      const normalizedName = item?.name ? String(item.name).trim().toLowerCase() : '';
      const matchingBreaksByName = item
        ? breaks.filter((b) =>
            Number(b.station_id) === Number(item.station_id)
            && String(b.name || '').trim().toLowerCase() === normalizedName
          )
        : [];

      setFormData(item ? {
        station_id: item.station_id || '',
        break_id: item.break_id || '',
        name: item.name || '',
        hour: item.hour || 0,
        minute: item.minute || 0,
        estimated_minute: item.estimated_minute || null,
        day_of_week: item.day_of_week !== undefined ? item.day_of_week : null,
        max_duration: item.max_duration || 60,
        min_duration: item.min_duration || 0,
        max_spots: item.max_spots || 10,
        break_type: item.break_type || 'commercial',
        priority: item.priority || 1,
        is_active: Boolean(item.is_active),
        allow_fillers: item.allow_fillers !== false,
        insert_jingles: item.insert_jingles || false,
        sync_networks: item.sync_networks || false,
        enable_topup: item.enable_topup !== false,
        topup_media_type: item.topup_media_type || 'spots',
        topup_custom_files: item.topup_custom_files || [],
        enable_backtiming: item.enable_backtiming || false,
        backtiming_start_minute: item.backtiming_start_minute || null,
        create_blank_playlist: item.create_blank_playlist || false,
        blank_playlist_path: item.blank_playlist_path || '',
        network_sync_enabled: item.network_sync_enabled || false,
        network_sync_stations: item.network_sync_stations || [],
        priority_activation_threshold: item.priority_activation_threshold || null,
        apply_to_selected_hours: true,
        selectedHours: normalizeHours(matchingBreaksByName.map((b) => b.hour)),
        apply_count: matchingBreaksByName.length || 1,
        range_start_hour: item.hour ?? 0,
        range_end_hour: item.hour ?? 0,
        range_step: 1,
      } : {
        station_id: '',
        break_id: null,
        name: '',
        hour: 0,
        minute: 0,
        selectedHours: [],
        estimated_minute: null,
        day_of_week: null,
        max_duration: 60,
        min_duration: 0,
        max_spots: 10,
        break_type: 'commercial',
        priority: 1,
        is_active: true,
        allow_fillers: true,
        insert_jingles: false,
        sync_networks: false,
        enable_topup: true,
        topup_media_type: 'spots',
        topup_custom_files: [],
        enable_backtiming: false,
        backtiming_start_minute: null,
        create_blank_playlist: false,
        blank_playlist_path: '',
        network_sync_enabled: false,
        network_sync_stations: [],
        priority_activation_threshold: null,
        apply_to_selected_hours: false,
        range_start_hour: 0,
        range_end_hour: 23,
        range_step: 1,
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const normalizeHours = (hours) => {
    if (!Array.isArray(hours)) return [];
    return [...new Set(
      hours
        .map((h) => parseInt(h, 10))
        .filter((h) => Number.isInteger(h) && h >= 0 && h <= 23)
    )].sort((a, b) => a - b);
  };

  const buildHoursFromRange = (start, end, step) => {
    const startHour = parseInt(start, 10);
    const endHour = parseInt(end, 10);
    const interval = parseInt(step, 10);

    if (!Number.isInteger(startHour) || !Number.isInteger(endHour) || !Number.isInteger(interval)) return [];
    if (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23 || interval < 1) return [];
    if (startHour > endHour) return [];

    const hours = [];
    for (let hour = startHour; hour <= endHour; hour += interval) {
      hours.push(hour);
    }
    return hours;
  };

  const handleSave = async () => {
    try {
        if (editingItem) {
          const updateData = { ...formData };
          delete updateData.selectedHours;
          delete updateData.range_start_hour;
          delete updateData.range_end_hour;
          delete updateData.range_step;

          if (formData.apply_to_selected_hours) {
            const originalName = String(editingItem?.name || '').trim().toLowerCase();
            const targetBreaks = breaks.filter((b) =>
              Number(b.station_id) === Number(editingItem.station_id)
              && String(b.name || '').trim().toLowerCase() === originalName
            );

            if (targetBreaks.length === 0) {
              alert('No matching breaks found for this break name.');
              return;
            }

            delete updateData.apply_to_selected_hours;
            delete updateData.hour;
            const operations = targetBreaks.map((breakItem) => ({
              action: 'update',
              id: breakItem.id,
              data: updateData,
            }));

            const bulkRes = await axios.post(
              `${API_URL}/api/advertising/breaks/bulk`,
              { operations },
              { withCredentials: true }
            );
            const updatedCount = bulkRes?.data?.counts?.updated ?? operations.length;
            setBulkBreakToast({
              open: true,
              severity: 'success',
              message: `Updated ${updatedCount} break(s).`,
            });
          } else {
            delete updateData.apply_to_selected_hours;

            // Editing existing break - single update
            await axios.put(
              `${API_URL}/api/advertising/breaks/${editingItem.id}`,
              updateData,
              { withCredentials: true }
            );
          }
        } else {
          // Creating new break(s) - support multiple hours
          const rangeHours = buildHoursFromRange(
            formData.range_start_hour,
            formData.range_end_hour,
            formData.range_step
          );
          const selectedHours = normalizeHours([
            ...(formData.selectedHours || []),
            ...rangeHours,
          ]);
          
          if (selectedHours.length === 0) {
            alert('Please select at least one hour for the break.');
            return;
          }
          
          // Create a break for each selected hour
          const operations = selectedHours.map((hour) => {
            const breakData = {
              ...formData,
              hour,
            };
            delete breakData.selectedHours;
            delete breakData.range_start_hour;
            delete breakData.range_end_hour;
            delete breakData.range_step;
            delete breakData.apply_to_selected_hours;

            return {
              action: 'create',
              data: breakData,
            };
          });

          const bulkRes = await axios.post(
            `${API_URL}/api/advertising/breaks/bulk`,
            { operations },
            { withCredentials: true }
          );
          const createdCount = bulkRes?.data?.counts?.created ?? operations.length;
          setBulkBreakToast({
            open: true,
            severity: 'success',
            message: `Created ${createdCount} break(s).`,
          });
        }
        fetchBreaks();

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving:', error);
      const msg = error.response?.data?.message || error.message;
      const detail = error.response?.data?.detail;
      alert('Failed to save: ' + msg + (detail ? ` (${detail})` : ''));
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      if (type === 'campaign') {
        await axios.delete(`${API_URL}/api/advertising/campaigns/${id}`, {
          withCredentials: true
        });
        fetchCampaigns();
      } else {
        await axios.delete(`${API_URL}/api/advertising/breaks/${id}`, {
          withCredentials: true
        });
        fetchBreaks();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete');
    }
  };

  const getSortComparator = (order, orderBy, getValue) => (a, b) => {
    const aVal = getValue ? getValue(a) : (a[orderBy] ?? '');
    const bVal = getValue ? getValue(b) : (b[orderBy] ?? '');
    const isAsc = order === 'asc';
    if (typeof aVal === 'number' && typeof bVal === 'number') return isAsc ? aVal - bVal : bVal - aVal;
    const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
    return isAsc ? cmp : -cmp;
  };

  const createSortHandler = (setState) => (field) => (e) => {
    setState(prev => ({
      orderBy: field,
      order: prev.orderBy === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const formatDateTimeSeconds = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const downloadPdfReport = () => {
    const rows = reportData?.rows || [];
    if (rows.length === 0) {
      alert('Run a report first.');
      return;
    }

    const stationName = reportFilters.station_id
      ? (stations.find((s) => String(s.id) === String(reportFilters.station_id))?.name || reportFilters.station_id)
      : 'All stations';
    const clientName = reportFilters.client || 'All clients';
    const campaignName = reportFilters.campaign_id
      ? (campaigns.find((c) => String(c.id) === String(reportFilters.campaign_id))?.name || reportFilters.campaign_id)
      : 'All campaigns';
    const dateFrom = reportFilters.date_from || reportData?.range?.from || '';
    const dateTo = reportFilters.date_to || reportData?.range?.to || dateFrom;
    const generatedAt = new Date().toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const detailRows = rows.map((row) => {
      const parentStation = stations.find((s) => String(s.id) === String(row.station_id))?.name || row.station_id || '-';
      const rowStation = row.play_source === 'franchise'
        ? `${parentStation} · franchise ${row.child_tenant_id || ''} station ${row.destination_station_id || ''}`.trim()
        : parentStation;
      const timeMain = reportType === 'scheduled'
        ? formatDateTimeSeconds(row.played_at || row.scheduled_time)
        : formatDateTimeSeconds(row.played_time);
      const timeSecondary = reportType === 'scheduled'
        ? (row.break_name || '-')
        : formatDateTimeSeconds(row.scheduled_time);
      return [
        row.client || '-',
        row.campaign_name || '-',
        row.spot_name || '-',
        String(rowStation),
        timeMain,
        timeSecondary,
      ];
    });

    const col5 = reportType === 'scheduled' ? 'Played / Scheduled Time' : 'Played Time';
    const col6 = reportType === 'scheduled' ? 'Break' : 'Scheduled Time';
    const reportTitle = reportType === 'scheduled' ? 'Scheduled Spots Report' : 'Played Spots Report';
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4',
    });

    doc.setFontSize(16);
    doc.text(reportTitle, 40, 40);
    doc.setFontSize(10);
    doc.text(`Station: ${stationName}`, 40, 62);
    doc.text(`Client: ${clientName}`, 40, 78);
    doc.text(`Campaign: ${campaignName}`, 40, 94);
    doc.text(`Date Range: ${dateFrom} to ${dateTo}`, 40, 110);
    doc.text(`Generated: ${generatedAt}`, 40, 126);
    doc.text(`Rows: ${rows.length}`, 40, 142);

    autoTable(doc, {
      startY: 160,
      head: [['Client', 'Campaign', 'Spot', 'Station', col5, col6]],
      body: detailRows,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [33, 33, 33] },
      margin: { left: 40, right: 40 },
    });

    const safeDate = (dateFrom || formatLocalDate()).replace(/[^0-9-]/g, '');
    doc.save(`advertising-report-${reportType}-${safeDate}.pdf`);
  };

  return (
    <Box>
      <PageHeader
        overline="Commercial"
        title="Advertising Management"
        subtitle="Manage campaigns, breaks and traffic across your stations"
        icon={<CampaignIcon />}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              disabled={!canEditAdvertising}
              onClick={() => {
                const stationId = trafficLogFilters.station_id || stations[0]?.id;
                if (!stationId) { alert('Select station first'); return; }
                setGenerateScheduleForm({
                  station_id: stationId,
                  date_from: trafficLogFilters.date || formatLocalDate(),
                  date_to: trafficLogFilters.date_end || trafficLogFilters.date || formatLocalDate()
                });
                setOpenGenerateScheduleDialog(true);
              }}
              sx={{
                backgroundImage: 'linear-gradient(180deg, #34d275 0%, #1fa855 100%)',
                color: '#04150b',
                '&:hover': { backgroundImage: 'linear-gradient(180deg, #46e087 0%, #26bb61 100%)', boxShadow: '0 6px 22px -6px rgba(34,197,94,0.55)' },
              }}
            >
              Generate Schedule
            </Button>
            <Button
              variant="outlined"
              startIcon={<BreakIcon />}
              disabled={!canEditAdvertising}
              onClick={() => handleOpenDialog()}
              sx={{
                borderColor: 'rgba(34,211,238,0.45)',
                color: 'var(--rs-accent)',
                '&:hover': { borderColor: '#22d3ee', bgcolor: 'rgba(34,211,238,0.08)' },
              }}
            >
              New Break
            </Button>
            <Button
              variant="contained"
              startIcon={<CampaignIcon />}
              disabled={!canEditAdvertising}
              onClick={() => handleOpenWizard(null, 0)}
              sx={{
                backgroundImage: 'linear-gradient(180deg, #4fdcf3 0%, #0ea5c9 100%)',
                color: '#04222b',
                '&:hover': { backgroundImage: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)', boxShadow: '0 6px 22px -6px rgba(34,211,238,0.6)' },
              }}
            >
              New Campaign
            </Button>
          </Box>
        }
      />

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: '1px solid rgba(148,163,184,0.12)' }}
      >
        <Tab label="Campaigns" icon={<CampaignIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Commercial Breaks" icon={<BreakIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        <Tab label="Traffic Log" />
        <Tab label="Reports" />
        <Tab label="Settings" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2.5 }}>
            {stations.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="campaign-station-filter-label">Filter by station</InputLabel>
                <Select
                  labelId="campaign-station-filter-label"
                  value={campaignStationFilter}
                  label="Filter by station"
                  onChange={(e) => setCampaignStationFilter(e.target.value)}
                  sx={{ bgcolor: 'rgba(148,163,184,0.05)' }}
                >
                  <MenuItem value="">All stations</MenuItem>
                  {stations.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <ToggleButtonGroup
              value={campaignSection}
              exclusive
              onChange={(e, v) => v && setCampaignSection(v)}
              size="small"
              sx={segmentedToggleSx}
            >
              <ToggleButton value="active" aria-label="active campaigns">Active</ToggleButton>
              <ToggleButton value="archive" aria-label="archived campaigns">
                <ArchiveIcon sx={{ mr: 0.5 }} fontSize="small" />
                Archive ({archivedCampaigns.length})
              </ToggleButton>
            </ToggleButtonGroup>
            {(activeCampaigns.length > 0 || archivedCampaigns.length > 0) && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  value={campaignViewMode}
                  exclusive
                  onChange={(e, v) => v && setCampaignViewMode(v)}
                  size="small"
                  sx={segmentedToggleSx}
                >
                  <ToggleButton value="card" aria-label="card view"><ViewModule sx={{ mr: 0.5 }} fontSize="small" /> Cards</ToggleButton>
                  <ToggleButton value="list" aria-label="list view"><ViewList sx={{ mr: 0.5 }} fontSize="small" /> List</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>
          {campaigns.length === 0 ? (
            <Card sx={{ borderStyle: 'dashed', borderColor: 'rgba(148,163,184,0.22)', bgcolor: 'rgba(22,27,37,0.5)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 56, height: 56, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(34,211,238,0.18), rgba(59,130,246,0.08))',
                      border: '1px solid rgba(34,211,238,0.3)',
                    }}
                  >
                    <CampaignIcon sx={{ color: 'var(--rs-accent)', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'var(--rs-text)' }}>No campaigns yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create your first campaign to start scheduling spots.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    disabled={!canEditAdvertising}
                    onClick={() => handleOpenWizard(null, 0)}
                    sx={{
                      mt: 1,
                      backgroundImage: 'linear-gradient(180deg, #4fdcf3 0%, #0ea5c9 100%)',
                      color: '#04222b',
                      '&:hover': { backgroundImage: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)' },
                    }}
                  >
                    New Campaign
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (() => {
            const displayCampaigns = campaignSection === 'archive' ? archivedCampaigns : activeCampaigns;
            const isEmpty = displayCampaigns.length === 0;
            const emptyMessage = campaignSection === 'archive'
              ? 'No archived campaigns. Finished or past-end-date campaigns appear here.'
              : 'No active campaigns. Check the Archive for finished campaigns.';
            return isEmpty ? (
              <Card sx={{ borderStyle: 'dashed', borderColor: 'rgba(148,163,184,0.22)', bgcolor: 'rgba(22,27,37,0.5)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1 }}>
                    <ArchiveIcon sx={{ color: '#5b6675', fontSize: 34 }} />
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : campaignViewMode === 'list' ? (
            <Card>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={campaignSort.orderBy === 'name' ? campaignSort.order : false}>
                        <TableSortLabel active={campaignSort.orderBy === 'name'} direction={campaignSort.orderBy === 'name' ? campaignSort.order : 'asc'} onClick={createSortHandler(setCampaignSort)('name')}>Name</TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={campaignSort.orderBy === 'client' ? campaignSort.order : false}>
                        <TableSortLabel active={campaignSort.orderBy === 'client'} direction={campaignSort.orderBy === 'client' ? campaignSort.order : 'asc'} onClick={createSortHandler(setCampaignSort)('client')}>Client</TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={campaignSort.orderBy === 'status' ? campaignSort.order : false}>
                        <TableSortLabel active={campaignSort.orderBy === 'status'} direction={campaignSort.orderBy === 'status' ? campaignSort.order : 'asc'} onClick={createSortHandler(setCampaignSort)('status')}>Status</TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...displayCampaigns].sort(getSortComparator(campaignSort.order, campaignSort.orderBy)).map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CampaignAvatar name={campaign.name} size={30} />
                            <Typography sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>{campaign.name}</Typography>
                            {campaign.is_syndicated && (
                              <Chip size="small" color="secondary" label="Mandatory · parent managed" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{campaign.client || '—'}</TableCell>
                        <TableCell>
                          <StatusChip status={campaign.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LibraryMusic />}
                            onClick={() => handleOpenWizard(campaign, 1)}
                            sx={{ borderColor: 'rgba(34,211,238,0.35)', color: 'var(--rs-accent)', mr: 0.5, '&:hover': { borderColor: '#22d3ee', bgcolor: 'rgba(34,211,238,0.08)' } }}
                          >
                            Spots
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenWizard(campaign, 2)}
                            sx={{ borderColor: 'rgba(192,132,252,0.35)', color: '#d8b4fe', mr: 0.5, '&:hover': { borderColor: '#c084fc', bgcolor: 'rgba(192,132,252,0.08)' } }}
                          >
                            Segments
                          </Button>
                          <IconButton
                            size="small"
                            disabled={!canEditAdvertising || campaign.is_syndicated}
                            onClick={() => handleOpenWizard(campaign, 0)}
                            sx={{ color: '#8b98ab', '&:hover': { color: 'var(--rs-accent)', bgcolor: 'rgba(34,211,238,0.10)' } }}
                            title="Edit campaign"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={!canEditAdvertising || campaign.is_syndicated}
                            onClick={() => handleDelete('campaign', campaign.id)}
                            sx={{ color: '#8b98ab', '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.10)' } }}
                            title="Delete campaign"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Grid container spacing={2.5}>
              {displayCampaigns.map((campaign) => {
                const hue = getCampaignHue(campaign.name);
                return (
                <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                  <Card
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, hsla(${hue}, 85%, 60%, 0.9), hsla(${hue + 50}, 85%, 60%, 0.15))`,
                      },
                      '&:hover': {
                        borderColor: `hsla(${hue}, 85%, 65%, 0.45)`,
                        transform: 'translateY(-3px)',
                        boxShadow: `0 1px 0 rgba(255,255,255,0.05) inset, 0 18px 40px -16px rgba(0,0,0,0.8), 0 0 24px -8px hsla(${hue}, 85%, 60%, 0.25)`,
                      },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2.5, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, mb: 2 }}>
                        <CampaignAvatar name={campaign.name} />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ color: 'var(--rs-text)', lineHeight: 1.25, wordBreak: 'break-word' }}>
                            {campaign.name}
                          </Typography>
                          {campaign.is_syndicated && (
                            <Chip size="small" color="secondary" label="Mandatory · parent managed" sx={{ mt: 0.5 }} />
                          )}
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }} noWrap>
                            {campaign.client || 'No client set'}
                          </Typography>
                        </Box>
                        <StatusChip status={campaign.status} />
                      </Box>

                      {campaign.total_spots ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                          <LibraryMusic sx={{ fontSize: 15, color: `hsl(${hue}, 80%, 70%)` }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <Box component="span" sx={{ color: 'var(--rs-text)', fontWeight: 600 }}>{campaign.total_spots}</Box> total spots
                          </Typography>
                        </Box>
                      ) : null}

                      <Divider sx={{ mt: 'auto', mb: 1.5, borderColor: 'rgba(148,163,184,0.10)' }} />

                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          startIcon={<LibraryMusic />}
                          onClick={() => handleOpenWizard(campaign, 1)}
                          sx={{
                            color: 'var(--rs-accent)',
                            bgcolor: 'rgba(34,211,238,0.08)',
                            border: '1px solid rgba(34,211,238,0.22)',
                            px: 1.5,
                            '&:hover': { bgcolor: 'rgba(34,211,238,0.16)', borderColor: 'rgba(34,211,238,0.45)' },
                          }}
                        >
                          Spots
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleOpenWizard(campaign, 2)}
                          sx={{
                            color: '#d8b4fe',
                            bgcolor: 'rgba(192,132,252,0.08)',
                            border: '1px solid rgba(192,132,252,0.22)',
                            px: 1.5,
                            '&:hover': { bgcolor: 'rgba(192,132,252,0.16)', borderColor: 'rgba(192,132,252,0.45)' },
                          }}
                        >
                          Segments
                        </Button>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            disabled={!canEditAdvertising || campaign.is_syndicated}
                            onClick={() => handleOpenWizard(campaign, 0)}
                            sx={{ color: '#8b98ab', '&:hover': { color: 'var(--rs-accent)', bgcolor: 'rgba(34,211,238,0.10)' } }}
                            title="Edit campaign"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={!canEditAdvertising || campaign.is_syndicated}
                            onClick={() => handleDelete('campaign', campaign.id)}
                            sx={{ color: '#8b98ab', '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.10)' } }}
                            title="Delete campaign"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                );
              })}
            </Grid>
          );
          })()}
        </Box>
      )}

      {tabValue === 1 && (
        <CommercialBreaksPanel
          breaks={breaks}
          stations={stations}
          canEdit={canEditAdvertising}
          onEdit={(breakItem) => handleOpenDialog(breakItem)}
          onDelete={(breakItem) => handleDelete('break', breakItem.id)}
          onCreate={() => handleOpenDialog()}
        />
      )}

      {tabValue === 2 && (
        <>
        <FranchiseBreakPanel
          stationId={trafficLogFilters.station_id}
          date={trafficLogFilters.date}
          refreshKey={trafficLog.length}
        />
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>Traffic Log Editor</Typography>
                <Typography variant="body2" color="text.secondary">
                  Plan by day and hour, choose a break, view what is in that break, and add spots manually when needed.
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={trafficLogFilters.station_id}
                    onChange={(e) => {
                      setTrafficLogFilters({ ...trafficLogFilters, station_id: e.target.value, break_id: '', region_id: '' });
                      setTrafficSelectedBreakItemId('');
                    }}
                    label="Station"
                  >
                    <MenuItem value="">All</MenuItem>
                    {stations.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {regionsForSelectedStation.length > 0 && (
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Region</InputLabel>
                    <Select
                      value={trafficLogFilters.region_id}
                      onChange={(e) => {
                        setTrafficLogFilters({ ...trafficLogFilters, region_id: e.target.value, break_id: '' });
                        setTrafficSelectedBreakItemId('');
                      }}
                      label="Region"
                    >
                      <MenuItem value="master">
                        <em>Master (all regions)</em>
                      </MenuItem>
                      {regionsForSelectedStation.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                      ))}
                      <MenuItem value="">All (combined view)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Day"
                  type="date"
                  value={trafficLogFilters.date}
                  onChange={(e) => {
                    setTrafficLogFilters({ ...trafficLogFilters, date: e.target.value, break_id: '' });
                    setTrafficSelectedBreakItemId('');
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Hour</InputLabel>
                  <Select
                    value={trafficSelectedHour}
                    onChange={(e) => {
                      setTrafficSelectedHour(parseInt(e.target.value, 10));
                      setTrafficLogFilters({ ...trafficLogFilters, break_id: '' });
                      setTrafficSelectedBreakItemId('');
                    }}
                    label="Hour"
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Break</InputLabel>
                  <Select
                    value={trafficLogFilters.break_id}
                    onChange={(e) => {
                      setTrafficLogFilters({ ...trafficLogFilters, break_id: e.target.value });
                      setTrafficSelectedBreakItemId('');
                    }}
                    label="Break"
                  >
                    <MenuItem value="">Select break</MenuItem>
                    {breaksForSelectedHour.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {`${String(b.hour).padStart(2, '0')}:${String(b.minute || 0).padStart(2, '0')} - ${b.name} (${b.max_duration}s)`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Break Contents</InputLabel>
                  <Select
                    value={trafficSelectedBreakItemId}
                    onChange={(e) => setTrafficSelectedBreakItemId(e.target.value)}
                    label="Break Contents"
                  >
                    <MenuItem value="">Select item in break</MenuItem>
                    {trafficBreakItems.map((row) => (
                      <MenuItem key={row.id} value={row.id}>
                        {`#${(row.position_in_break ?? 0) + 1} - ${row.spot_name || 'Spot'} (${row.campaign_name || 'Campaign'})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Manual Add: Campaign</InputLabel>
                  <Select
                    value={trafficManualAdd.campaign_id}
                    onChange={(e) => setTrafficManualAdd({ campaign_id: e.target.value, spot_id: '' })}
                    label="Manual Add: Campaign"
                  >
                    <MenuItem value="">Select campaign</MenuItem>
                    {campaigns.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!trafficManualAdd.campaign_id}>
                  <InputLabel>Manual Add: Spot</InputLabel>
                  <Select
                    value={trafficManualAdd.spot_id}
                    onChange={(e) => setTrafficManualAdd({ ...trafficManualAdd, spot_id: e.target.value })}
                    label="Manual Add: Spot"
                  >
                    <MenuItem value="">Select spot</MenuItem>
                    {trafficManualSpots.map((spot) => (
                      <MenuItem key={spot.id} value={spot.id}>{spot.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Add />}
                    disabled={!canEditAdvertising}
                    onClick={handleManualAddSpotToSelectedBreak}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                  >
                    Add Spot to Selected Break
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      if (!trafficSelectedBreakItemId) return;
                      const item = trafficBreakItems.find((r) => String(r.id) === String(trafficSelectedBreakItemId));
                      if (item) setSelectedScheduledAd(item);
                    }}
                  >
                    View Selected
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {selectedTrafficBreak && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'var(--rs-surface)', borderRadius: 1, border: '1px solid var(--rs-border)', display: 'flex', gap: 3, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#22d3ee' }}>
                  <strong>{selectedTrafficBreak.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Break Length: <strong style={{ color: 'var(--rs-text)' }}>{selectedTrafficBreak.max_duration}s</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Max Spots: <strong style={{ color: 'var(--rs-text)' }}>{selectedTrafficBreak.max_spots}</strong>
                </Typography>
                {trafficLogFilters.region_id && trafficLogFilters.region_id !== 'master' && (
                  <Chip
                    label={`Region: ${regions.find(r => r.id === parseInt(trafficLogFilters.region_id))?.name || trafficLogFilters.region_id}`}
                    size="small"
                    sx={{ bgcolor: '#f59e0b20', color: '#f59e0b', border: '1px solid #f59e0b40' }}
                  />
                )}
                {(!trafficLogFilters.region_id || trafficLogFilters.region_id === 'master') && regionsForSelectedStation.length > 0 && (
                  <Chip
                    label="Master Stream"
                    size="small"
                    sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', border: '1px solid #22d3ee40' }}
                  />
                )}
              </Box>
            )}
            <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid var(--rs-border)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'scheduled_time' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'scheduled_time'} direction={trafficLogSort.orderBy === 'scheduled_time' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('scheduled_time')}>Time</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'campaign_name' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'campaign_name'} direction={trafficLogSort.orderBy === 'campaign_name' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('campaign_name')}>Campaign</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'spot_name' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'spot_name'} direction={trafficLogSort.orderBy === 'spot_name' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('spot_name')}>Spot</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'station' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'station'} direction={trafficLogSort.orderBy === 'station' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('station')}>Station</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'region_name' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'region_name'} direction={trafficLogSort.orderBy === 'region_name' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('region_name')}>Region</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'break_name' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'break_name'} direction={trafficLogSort.orderBy === 'break_name' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('break_name')}>Break</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'position_in_break' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'position_in_break'} direction={trafficLogSort.orderBy === 'position_in_break' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('position_in_break')}>Position</TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={trafficLogSort.orderBy === 'status' ? trafficLogSort.order : false}>
                      <TableSortLabel active={trafficLogSort.orderBy === 'status'} direction={trafficLogSort.orderBy === 'status' ? trafficLogSort.order : 'asc'} onClick={createSortHandler(setTrafficLogSort)('status')}>Status</TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trafficRowsForView.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No scheduled ads for selected day/hour/break{trafficLogFilters.region_id && trafficLogFilters.region_id !== 'master' ? ` in ${regions.find(r => r.id === parseInt(trafficLogFilters.region_id))?.name || 'this region'}` : ''}. Add manually or generate schedule.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (trafficLogFilters.break_id
                      ? [...trafficRowsForView].sort((a, b) => (a.position_in_break ?? 0) - (b.position_in_break ?? 0))
                      : [...trafficRowsForView].sort(getSortComparator(trafficLogSort.order, trafficLogSort.orderBy, (row) => {
                          if (trafficLogSort.orderBy === 'station') return stations.find(s => s.id === row.station_id)?.name ?? '';
                          if (trafficLogSort.orderBy === 'region_name') return row.region_name ?? 'Master';
                          if (trafficLogSort.orderBy === 'break_name') return row.break_name ?? '';
                          if (trafficLogSort.orderBy === 'scheduled_time') return row.scheduled_time ? new Date(row.scheduled_time).getTime() : 0;
                          if (trafficLogSort.orderBy === 'status') return row.played_at ? 'played' : (row.status || 'scheduled');
                          return row[trafficLogSort.orderBy] ?? '';
                        }))).map((row) => (
                      <TableRow
                        key={row.id}
                        draggable={!!trafficLogFilters.break_id && !row.is_mandatory && canEditAdvertising}
                        onDragStart={() => !row.is_mandatory && handleTrafficDragStart(row.id)}
                        onDragOver={(e) => trafficLogFilters.break_id && !row.is_mandatory && e.preventDefault()}
                        onDrop={() => !row.is_mandatory && handleTrafficDrop(row.id)}
                        sx={{
                          cursor: trafficLogFilters.break_id && !row.is_mandatory ? 'grab' : 'default',
                          opacity: trafficDragScheduledId === row.id ? 0.6 : 1,
                        }}
                      >
                        <TableCell>{row.scheduled_time ? new Date(row.scheduled_time).toLocaleString() : '—'}</TableCell>
                        <TableCell>
                          {row.campaign_name || '—'}
                          {row.is_mandatory && (
                            <Chip size="small" color="secondary" label="Mandatory · parent managed" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{row.spot_name || '—'}</TableCell>
                        <TableCell>{stations.find(s => s.id === row.station_id)?.name || row.station_id}</TableCell>
                        <TableCell>
                          {row.region_name ? (
                            <Chip label={row.region_name} size="small" sx={{ bgcolor: '#f59e0b20', color: '#f59e0b', fontSize: '0.75rem' }} />
                          ) : (
                            <Chip label="Master" size="small" sx={{ bgcolor: '#22d3ee15', color: '#22d3ee80', fontSize: '0.75rem' }} />
                          )}
                        </TableCell>
                        <TableCell>{row.break_name ?? '—'}</TableCell>
                        <TableCell>{row.position_in_break ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || 'scheduled'}
                            size="small"
                            sx={{
                              bgcolor: row.played_at ? '#00ff0020' : '#22d3ee20',
                              color: row.played_at ? '#00ff00' : '#22d3ee',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" sx={{ color: '#22d3ee' }} onClick={() => setSelectedScheduledAd(row)} title="View details">
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={row.is_mandatory || !canEditAdvertising}
                            sx={{ color: '#ff6b6b' }}
                            onClick={async () => {
                            try {
                              await axios.post(
                                `${API_URL}/api/advertising/traffic-log/bulk`,
                                { operations: [{ action: 'delete', id: row.id }] },
                                { withCredentials: true }
                              );
                              fetchTrafficLog();
                            } catch (e) { alert('Failed to delete'); }
                          }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        </>
      )}

      {/* Scheduled Ad Detail Dialog */}
      <Dialog open={!!selectedScheduledAd} onClose={() => setSelectedScheduledAd(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#22d3ee' }}>Scheduled Ad Details</DialogTitle>
        <DialogContent>
          {selectedScheduledAd && (
            <Box sx={{ pt: 1 }}>
              {selectedScheduledAd.is_mandatory && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This mandatory airing is managed by the parent tenant and cannot be moved or deleted locally.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Scheduled time</Typography>
                  <Typography>{selectedScheduledAd.scheduled_time ? new Date(selectedScheduledAd.scheduled_time).toLocaleString() : '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Station</Typography>
                  <Typography>{stations.find(s => s.id === selectedScheduledAd.station_id)?.name || selectedScheduledAd.station_id || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Region</Typography>
                  <Typography>
                    {selectedScheduledAd.region_name ? (
                      <Chip label={selectedScheduledAd.region_name} size="small" sx={{ bgcolor: '#f59e0b20', color: '#f59e0b' }} />
                    ) : (
                      <Chip label="Master" size="small" sx={{ bgcolor: '#22d3ee15', color: '#22d3ee80' }} />
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Campaign</Typography>
                  <Typography>{selectedScheduledAd.campaign_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Spot</Typography>
                  <Typography>{selectedScheduledAd.spot_name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Break</Typography>
                  <Typography>
                    {(() => {
                      const brk = breaks.find(b => b.id === selectedScheduledAd.break_id);
                      if (!selectedScheduledAd.break_id || !brk) return '—';
                      const st = stations.find(s => s.id === brk.station_id);
                      return `${st?.name || ''} · ${String(brk.hour || 0).padStart(2, '0')}:${String(brk.minute || 0).padStart(2, '0')} · ${brk.name || ''}`;
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Position in break</Typography>
                  <Typography>{selectedScheduledAd.position_in_break ?? '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography>
                    <Chip
                      label={selectedScheduledAd.status || (selectedScheduledAd.played_at ? 'played' : 'scheduled')}
                      size="small"
                      sx={{
                        bgcolor: selectedScheduledAd.played_at ? '#00ff0020' : '#22d3ee20',
                        color: selectedScheduledAd.played_at ? '#00ff00' : '#22d3ee',
                      }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Played at</Typography>
                  <Typography>{selectedScheduledAd.played_at ? new Date(selectedScheduledAd.played_at).toLocaleString() : '—'}</Typography>
                </Grid>
                {selectedScheduledAd.override_reason && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Override reason</Typography>
                    <Typography>{selectedScheduledAd.override_reason}</Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography>{selectedScheduledAd.created_at ? new Date(selectedScheduledAd.created_at).toLocaleString() : '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Updated</Typography>
                  <Typography>{selectedScheduledAd.updated_at ? new Date(selectedScheduledAd.updated_at).toLocaleString() : '—'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedScheduledAd(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {tabValue === 3 && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>Advertising Reports</Typography>
                <Typography variant="body2" color="text.secondary">
                  Reports use the <strong>Client</strong> name set on each campaign. Filter by client, date range, station, or campaign. Use browser print to export as PDF.
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={reportType}
                exclusive
                onChange={(e, v) => v && setReportType(v)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { color: '#999' }, '& .Mui-selected': { color: '#22d3ee !important' } }}
              >
                <ToggleButton value="scheduled">Scheduled</ToggleButton>
                <ToggleButton value="played">Played</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={reportFilters.station_id}
                    onChange={(e) => setReportFilters({ ...reportFilters, station_id: e.target.value })}
                    label="Station"
                  >
                    <MenuItem value="">All</MenuItem>
                    {stations.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date (from)"
                  type="date"
                  value={reportFilters.date_from}
                  onChange={(e) => setReportFilters({ ...reportFilters, date_from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date (to)"
                  type="date"
                  value={reportFilters.date_to}
                  onChange={(e) => setReportFilters({ ...reportFilters, date_to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Client (from campaign)</InputLabel>
                  <Select
                    value={reportFilters.client}
                    onChange={(e) => {
                      const selectedClient = e.target.value;
                      // Keep campaign/client linked: client change resets campaign filter.
                      setReportFilters({
                        ...reportFilters,
                        client: selectedClient,
                        campaign_id: '',
                      });
                    }}
                    label="Client (from campaign)"
                  >
                    <MenuItem value="">All clients</MenuItem>
                    {reportClients.map((name) => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={reportFilters.campaign_id}
                    onChange={(e) => {
                      const selectedCampaignId = e.target.value;
                      const selectedCampaign = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
                      // Keep campaign/client linked: selecting campaign sets its client.
                      setReportFilters({
                        ...reportFilters,
                        campaign_id: selectedCampaignId,
                        client: selectedCampaign?.client || reportFilters.client || '',
                      });
                    }}
                    label="Campaign"
                  >
                    <MenuItem value="">All</MenuItem>
                    {reportCampaignOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {reportError && (
              <Alert severity="error" onClose={() => setReportError(null)} sx={{ mb: 2 }}>
                {reportError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={downloadPdfReport}
                disabled={reportLoading || !reportData?.rows?.length}
                sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setReportFilters({
                    station_id: '',
                    client: '',
                    campaign_id: '',
                    date_from: formatLocalDate(),
                    date_to: formatLocalDate(),
                  });
                  setReportData({ range: null, rows: [], count: 0 });
                  setReportError(null);
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={fetchReport}
                disabled={reportLoading}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                {reportLoading ? 'Generating…' : 'Run Report'}
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-surface)', border: '1px solid var(--rs-border)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client (from campaign)</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Spot</TableCell>
                    <TableCell>Station</TableCell>
                    {reportType === 'scheduled' ? (
                      <>
                        <TableCell>Played / Scheduled Time</TableCell>
                        <TableCell>Break</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Played Time</TableCell>
                        <TableCell>Scheduled Time</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!reportData.rows || reportData.rows.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No report data for selected filters. Adjust filters and run the report.
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.rows.map((row, idx) => (
                      <TableRow key={row.id || row.playout_log_id || idx}>
                        <TableCell>{row.client || '—'}</TableCell>
                        <TableCell>{row.campaign_name || '—'}</TableCell>
                        <TableCell>{row.spot_name || '—'}</TableCell>
                        <TableCell>
                          {stations.find(s => s.id === row.station_id)?.name || row.station_id || '—'}
                          {row.play_source === 'franchise' && (
                            <Chip
                              size="small"
                              color="secondary"
                              label={`Franchise ${row.child_tenant_id || ''} · station ${row.destination_station_id || '—'}`}
                              sx={{ ml: 1 }}
                            />
                          )}
                        </TableCell>
                        {reportType === 'scheduled' ? (
                          <>
                            <TableCell>{formatDateTimeSeconds(row.played_at || row.scheduled_time)}</TableCell>
                            <TableCell>{row.break_name || '—'}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{formatDateTimeSeconds(row.played_time)}</TableCell>
                            <TableCell>{formatDateTimeSeconds(row.scheduled_time)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tabValue === 4 && (
        <>
        <ExternalAdvertisingSettings
          stationId={externalAdStationId || (stations.length === 1 ? stations[0]?.id : '')}
          stations={stations}
          canManage={canManageExternalAdvertising}
          onStationChange={setExternalAdStationId}
        />
        <AutoGenerationSchedules
          jobType="traffic"
          stations={stations}
          canEdit={canEditAdvertising}
        />
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 3 }}>Advertising Settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure day parts and business categories for use in campaigns and segments.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>Day Parts</Typography>
                  <Button variant="outlined" size="small" startIcon={<Add />} disabled={!canEditAdvertising} onClick={() => { setEditingDaypart(null); setDaypartForm({ name: '', start_time: '06:00', end_time: '10:00', station_id: '' }); setDaypartDialogOpen(true); }} sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}>
                    Add
                  </Button>
                </Box>
                <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-surface)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={daypartSort.orderBy === 'name' ? daypartSort.order : false}>
                          <TableSortLabel active={daypartSort.orderBy === 'name'} direction={daypartSort.orderBy === 'name' ? daypartSort.order : 'asc'} onClick={createSortHandler(setDaypartSort)('name')}>Name</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daypartSort.orderBy === 'start_time' ? daypartSort.order : false}>
                          <TableSortLabel active={daypartSort.orderBy === 'start_time'} direction={daypartSort.orderBy === 'start_time' ? daypartSort.order : 'asc'} onClick={createSortHandler(setDaypartSort)('start_time')}>Start</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={daypartSort.orderBy === 'end_time' ? daypartSort.order : false}>
                          <TableSortLabel active={daypartSort.orderBy === 'end_time'} direction={daypartSort.orderBy === 'end_time' ? daypartSort.order : 'asc'} onClick={createSortHandler(setDaypartSort)('end_time')}>End</TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...dayparts].sort(getSortComparator(daypartSort.order, daypartSort.orderBy)).map((dp) => (
                        <TableRow key={dp.id}>
                          <TableCell>{dp.name}</TableCell>
                          <TableCell>{dp.start_time}</TableCell>
                          <TableCell>{dp.end_time}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" disabled={!canEditAdvertising} onClick={() => { setEditingDaypart(dp); setDaypartForm({ name: dp.name, start_time: dp.start_time || '06:00', end_time: dp.end_time || '10:00', station_id: dp.station_id || '' }); setDaypartDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" disabled={!canEditAdvertising} onClick={async () => {
                              if (!window.confirm('Delete?')) return;
                              try {
                                await axios.delete(`${API_URL}/api/advertising/dayparts/${dp.id}`, { withCredentials: true });
                                fetchDayparts();
                              } catch (error) {
                                alert(error.response?.data?.message || 'Failed to delete day part');
                              }
                            }}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {dayparts.length === 0 && <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No day parts. Add one for use in segments.</Typography>}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ color: '#22d3ee' }}>Business Categories</Typography>
                  <Button variant="outlined" size="small" startIcon={<Add />} disabled={!canEditAdvertising} onClick={() => { setEditingBusinessCat(null); setBusinessCatForm({ name: '', description: '' }); setBusinessCatDialogOpen(true); }} sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}>
                    Add
                  </Button>
                </Box>
                <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-surface)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={businessCatSort.orderBy === 'name' ? businessCatSort.order : false}>
                          <TableSortLabel active={businessCatSort.orderBy === 'name'} direction={businessCatSort.orderBy === 'name' ? businessCatSort.order : 'asc'} onClick={createSortHandler(setBusinessCatSort)('name')}>Name</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={businessCatSort.orderBy === 'description' ? businessCatSort.order : false}>
                          <TableSortLabel active={businessCatSort.orderBy === 'description'} direction={businessCatSort.orderBy === 'description' ? businessCatSort.order : 'asc'} onClick={createSortHandler(setBusinessCatSort)('description')}>Description</TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...businessCategories].sort(getSortComparator(businessCatSort.order, businessCatSort.orderBy)).map((bc) => (
                        <TableRow key={bc.id}>
                          <TableCell>{bc.name}</TableCell>
                          <TableCell>{bc.description || '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" disabled={!canEditAdvertising} onClick={() => { setEditingBusinessCat(bc); setBusinessCatForm({ name: bc.name, description: bc.description || '' }); setBusinessCatDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" disabled={!canEditAdvertising} onClick={async () => {
                              if (!window.confirm('Delete?')) return;
                              try {
                                await axios.delete(`${API_URL}/api/advertising/business-categories/${bc.id}`, { withCredentials: true });
                                fetchBusinessCategories();
                              } catch (error) {
                                alert(error.response?.data?.message || 'Failed to delete business category');
                              }
                            }}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {businessCategories.length === 0 && <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No business categories. Add one for ad separation.</Typography>}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </>
      )}

      {/* Day Part Dialog */}
      <Dialog open={daypartDialogOpen} onClose={() => setDaypartDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingDaypart ? 'Edit Day Part' : 'Add Day Part'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={daypartForm.name} onChange={(e) => setDaypartForm({ ...daypartForm, name: e.target.value })} placeholder="e.g. Morning Drive" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start (HH:MM)" type="time" value={daypartForm.start_time} onChange={(e) => setDaypartForm({ ...daypartForm, start_time: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End (HH:MM)" type="time" value={daypartForm.end_time} onChange={(e) => setDaypartForm({ ...daypartForm, end_time: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDaypartDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (editingDaypart) {
                await axios.put(`${API_URL}/api/advertising/dayparts/${editingDaypart.id}`, daypartForm, { withCredentials: true });
              } else {
                await axios.post(`${API_URL}/api/advertising/dayparts`, daypartForm, { withCredentials: true });
              }
              setDaypartDialogOpen(false);
              fetchDayparts();
            } catch (e) { alert(e.response?.data?.message || 'Failed'); }
          }} disabled={!canEditAdvertising} sx={{ bgcolor: '#22d3ee' }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Business Category Dialog */}
      <Dialog open={businessCatDialogOpen} onClose={() => setBusinessCatDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingBusinessCat ? 'Edit Business Category' : 'Add Business Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={businessCatForm.name} onChange={(e) => setBusinessCatForm({ ...businessCatForm, name: e.target.value })} placeholder="e.g. Automotive" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline value={businessCatForm.description} onChange={(e) => setBusinessCatForm({ ...businessCatForm, description: e.target.value })} placeholder="Optional" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBusinessCatDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (editingBusinessCat) {
                await axios.put(`${API_URL}/api/advertising/business-categories/${editingBusinessCat.id}`, businessCatForm, { withCredentials: true });
              } else {
                await axios.post(`${API_URL}/api/advertising/business-categories`, businessCatForm, { withCredentials: true });
              }
              setBusinessCatDialogOpen(false);
              fetchBusinessCategories();
            } catch (e) { alert(e.response?.data?.message || 'Failed'); }
          }} disabled={!canEditAdvertising} sx={{ bgcolor: '#22d3ee' }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Commercial Break Dialog (campaigns use the CampaignWizard) */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Commercial Break
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Station *</InputLabel>
                    <Select
                      value={formData.station_id || ''}
                      onChange={(e) => setFormData({ ...formData, station_id: parseInt(e.target.value) })}
                      required
                    >
                      {stations.map((station) => (
                        <MenuItem key={station.id} value={station.id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Break ID (1-20) *"
                    type="number"
                    inputProps={{ min: 1, max: 20 }}
                    value={formData.break_id || ''}
                    onChange={(e) => setFormData({ ...formData, break_id: parseInt(e.target.value) || 1 })}
                    helperText="Unique ID for this break (1-20 per hour)"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Day of Week</InputLabel>
                    <Select
                      value={formData.day_of_week !== null && formData.day_of_week !== undefined ? formData.day_of_week : -1}
                      onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value === -1 ? null : e.target.value })}
                      label="Day of Week"
                    >
                      <MenuItem value={-1}>All Days</MenuItem>
                      <MenuItem value={0}>Sunday</MenuItem>
                      <MenuItem value={1}>Monday</MenuItem>
                      <MenuItem value={2}>Tuesday</MenuItem>
                      <MenuItem value={3}>Wednesday</MenuItem>
                      <MenuItem value={4}>Thursday</MenuItem>
                      <MenuItem value={5}>Friday</MenuItem>
                      <MenuItem value={6}>Saturday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Break Name *"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                {editingItem ? (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!formData.apply_to_selected_hours}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setFormData({
                                ...formData,
                                apply_to_selected_hours: enabled,
                              });
                            }}
                          />
                        }
                        label="Bulk edit all breaks with this same name (same station)"
                      />
                    </Grid>
                    {formData.apply_to_selected_hours ? (
                      <Grid item xs={12} sm={8}>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Changes will apply to {formData.apply_count || 1} break(s) that share this name on the selected station.
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Hour"
                          type="number"
                          inputProps={{ min: 0, max: 23 }}
                          value={formData.hour || 0}
                          onChange={(e) => setFormData({ ...formData, hour: parseInt(e.target.value, 10) || 0 })}
                        />
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={8}>
                      <FormControl fullWidth>
                        <InputLabel>Hours *</InputLabel>
                        <Select
                          multiple
                          value={formData.selectedHours || []}
                          onChange={(e) => setFormData({ ...formData, selectedHours: e.target.value })}
                          input={<OutlinedInput label="Hours *" />}
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) return '';
                            const normalized = normalizeHours(selected);
                            if (normalized.length <= 6) {
                              return normalized.map(h => `${h.toString().padStart(2, '0')}:00`).join(', ');
                            }
                            return `${normalized.length} hours selected`;
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                            <MenuItem key={hour} value={hour}>
                              <Checkbox checked={(formData.selectedHours || []).map((h) => parseInt(h, 10)).includes(hour)} />
                              <ListItemText primary={`${hour.toString().padStart(2, '0')}:00`} />
                            </MenuItem>
                          ))}
                        </Select>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setFormData({ ...formData, selectedHours: Array.from({ length: 24 }, (_, i) => i) })}
                          >
                            Select All Hours
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setFormData({ ...formData, selectedHours: [] })}
                          >
                            Clear All
                          </Button>
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Quick Range</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                        <TextField
                          label="Start"
                          type="number"
                          inputProps={{ min: 0, max: 23 }}
                          value={formData.range_start_hour ?? 0}
                          onChange={(e) => setFormData({ ...formData, range_start_hour: parseInt(e.target.value, 10) || 0 })}
                          size="small"
                        />
                        <TextField
                          label="End"
                          type="number"
                          inputProps={{ min: 0, max: 23 }}
                          value={formData.range_end_hour ?? 23}
                          onChange={(e) => setFormData({ ...formData, range_end_hour: parseInt(e.target.value, 10) || 0 })}
                          size="small"
                        />
                        <TextField
                          label="Every"
                          type="number"
                          inputProps={{ min: 1, max: 23 }}
                          value={formData.range_step ?? 1}
                          onChange={(e) => setFormData({ ...formData, range_step: parseInt(e.target.value, 10) || 1 })}
                          size="small"
                        />
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          const hours = buildHoursFromRange(formData.range_start_hour, formData.range_end_hour, formData.range_step);
                          if (hours.length === 0) {
                            alert('Invalid range. Use 0-23 and End >= Start.');
                            return;
                          }
                          setFormData({ ...formData, selectedHours: hours });
                        }}
                      >
                        Apply Range
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Configure once and create all hour-based breaks at once.
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={editingItem ? 4 : 4}>
                  <TextField
                    fullWidth
                    label="Minute"
                    type="number"
                    inputProps={{ min: 0, max: 59 }}
                    value={formData.minute || 0}
                    onChange={(e) => setFormData({ ...formData, minute: parseInt(e.target.value) || 0 })}
                    helperText="Start minute for breaks"
                  />
                </Grid>
                <Grid item xs={12} sm={editingItem ? 4 : 12}>
                  <TextField
                    fullWidth
                    label="Max Duration (s) *"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.max_duration || 60}
                    onChange={(e) => setFormData({ ...formData, max_duration: parseFloat(e.target.value) || 60 })}
                    required
                    helperText="Length of break"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Min Duration (s)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.min_duration || 0}
                    onChange={(e) => setFormData({ ...formData, min_duration: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Estimated Minute"
                    type="number"
                    inputProps={{ min: 0, max: 59 }}
                    value={formData.estimated_minute || ''}
                    onChange={(e) => setFormData({ ...formData, estimated_minute: e.target.value === '' ? null : parseInt(e.target.value) })}
                    helperText="Optional scheduling reference"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Max Spots"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.max_spots || 10}
                    onChange={(e) => setFormData({ ...formData, max_spots: parseInt(e.target.value) || 10 })}
                    helperText="Maximum number of spots per break"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Priority (1-5)"
                    type="number"
                    inputProps={{ min: 1, max: 5 }}
                    value={formData.priority || 1}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                    helperText="Higher priority fills first"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Priority Activation Threshold (s)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={formData.priority_activation_threshold || ''}
                    onChange={(e) => setFormData({ ...formData, priority_activation_threshold: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    helperText="Priority 2+ breaks activate when Priority 1 reaches this duration"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#22d3ee' }}>
                    Top-Up Settings
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_topup !== false}
                        onChange={(e) => setFormData({ ...formData, enable_topup: e.target.checked })}
                      />
                    }
                    label="Enable Top-Up"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                    Use active spots or custom media to fill shorter breaks
                  </Typography>
                </Grid>
                {formData.enable_topup && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Top-Up Media Type</InputLabel>
                        <Select
                          value={formData.topup_media_type || 'spots'}
                          onChange={(e) => setFormData({ ...formData, topup_media_type: e.target.value })}
                          label="Top-Up Media Type"
                        >
                          <MenuItem value="spots">Active Spots</MenuItem>
                          <MenuItem value="custom">Custom Media Files</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.topup_media_type === 'custom' && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Custom Media Files (JSON array of file paths)
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={Array.isArray(formData.topup_custom_files) ? JSON.stringify(formData.topup_custom_files, null, 2) : ''}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              if (Array.isArray(parsed)) {
                                setFormData({ ...formData, topup_custom_files: parsed });
                              }
                            } catch {
                              // Invalid JSON, ignore
                            }
                          }}
                          placeholder='["/path/to/file1.mp3", "/path/to/file2.mp3"]'
                        />
                      </Grid>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#22d3ee' }}>
                    Blank Playlist (Commercial-Free Breaks)
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.create_blank_playlist || false}
                        onChange={(e) => setFormData({ ...formData, create_blank_playlist: e.target.checked })}
                      />
                    }
                    label="Create Blank Playlist File"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                    Generate blank playlist file for commercial-free breaks
                  </Typography>
                </Grid>
                {formData.create_blank_playlist && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Blank Playlist Path"
                      value={formData.blank_playlist_path || ''}
                      onChange={(e) => setFormData({ ...formData, blank_playlist_path: e.target.value })}
                      helperText="Path where blank playlist file will be created"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#22d3ee' }}>
                    Network Synchronization
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.network_sync_enabled || false}
                        onChange={(e) => setFormData({ ...formData, network_sync_enabled: e.target.checked })}
                      />
                    }
                    label="Enable Network Synchronization"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                    Sync break duration settings with other stations in network
                  </Typography>
                </Grid>
                {formData.network_sync_enabled && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Sync Stations</InputLabel>
                      <Select
                        multiple
                        value={Array.isArray(formData.network_sync_stations) ? formData.network_sync_stations : []}
                        onChange={(e) => setFormData({ ...formData, network_sync_stations: e.target.value })}
                        label="Sync Stations"
                        renderValue={(selected) => selected.map(id => stations.find(s => s.id === id)?.name || id).join(', ')}
                      >
                        {stations.filter(s => s.id !== formData.station_id).map((station) => (
                          <MenuItem key={station.id} value={station.id}>
                            {station.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: '#22d3ee' }}>
                    Additional Settings
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active !== false}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allow_fillers !== false}
                        onChange={(e) => setFormData({ ...formData, allow_fillers: e.target.checked })}
                      />
                    }
                    label="Allow Fillers"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.insert_jingles || false}
                        onChange={(e) => setFormData({ ...formData, insert_jingles: e.target.checked })}
                      />
                    }
                    label="Insert Jingles"
                  />
                </Grid>
              </>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canEditAdvertising} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified Campaign Wizard: details → spots → schedule (segments) */}
      <CampaignWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        campaign={wizardCampaign}
        initialStep={wizardStep}
        stations={stations}
        regions={regions}
        breaks={breaks}
        franchiseTargets={franchiseTargets}
        businessCategories={businessCategories}
        dayparts={dayparts}
        segmentTemplates={segmentTemplates}
        canEdit={canEditAdvertising}
        onSaved={fetchCampaigns}
        onTemplatesChanged={fetchSegmentTemplates}
        onToast={alert}
      />

      {/* Add Scheduled Ad Dialog */}
      <Dialog open={openAddScheduledDialog} onClose={() => setOpenAddScheduledDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Scheduled Ad</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Station *</InputLabel>
                <Select
                  value={scheduledAdForm.station_id}
                  onChange={(e) => setScheduledAdForm({ ...scheduledAdForm, station_id: e.target.value })}
                  label="Station *"
                >
                  {stations.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Campaign</InputLabel>
                <Select
                  value={scheduledAdForm.campaign_id}
                  onChange={async (e) => {
                    const cid = e.target.value;
                    setScheduledAdForm({ ...scheduledAdForm, campaign_id: cid, spot_id: '' });
                    setScheduledAdSpots([]);
                    if (cid) {
                      try {
                        const res = await axios.get(`${API_URL}/api/advertising/campaigns/${cid}/spots`, { withCredentials: true });
                        setScheduledAdSpots(res.data || []);
                      } catch (er) { setScheduledAdSpots([]); }
                    }
                  }}
                  label="Campaign"
                >
                  <MenuItem value="">—</MenuItem>
                  {campaigns.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {scheduledAdForm.campaign_id && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Spot</InputLabel>
                  <Select
                    value={scheduledAdForm.spot_id}
                    onChange={(e) => setScheduledAdForm({ ...scheduledAdForm, spot_id: e.target.value })}
                    label="Spot"
                  >
                    <MenuItem value="">—</MenuItem>
                    {scheduledAdSpots.filter((s) => !s.is_placeholder).map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name} ({s.duration}s)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Scheduled Time *"
                type="datetime-local"
                value={scheduledAdForm.scheduled_time ? scheduledAdForm.scheduled_time.slice(0, 16) : ''}
                onChange={(e) => setScheduledAdForm({ ...scheduledAdForm, scheduled_time: e.target.value || '' })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Position in Break"
                type="number"
                inputProps={{ min: 0 }}
                value={scheduledAdForm.position_in_break}
                onChange={(e) => setScheduledAdForm({ ...scheduledAdForm, position_in_break: parseInt(e.target.value) || 0 })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddScheduledDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            if (!scheduledAdForm.station_id || !scheduledAdForm.scheduled_time) {
              alert('Station and scheduled time required');
              return;
            }
            try {
              await axios.post(`${API_URL}/api/advertising/traffic-log/bulk`, {
                operations: [{
                  action: 'create',
                  data: {
                    station_id: scheduledAdForm.station_id,
                    campaign_id: scheduledAdForm.campaign_id || null,
                    spot_id: scheduledAdForm.spot_id || null,
                    scheduled_time: scheduledAdForm.scheduled_time,
                    position_in_break: scheduledAdForm.position_in_break,
                  },
                }],
              }, { withCredentials: true });
              setOpenAddScheduledDialog(false);
              fetchTrafficLog();
            } catch (e) { alert('Failed: ' + (e.response?.data?.message || e.message)); }
          }} sx={{ bgcolor: '#22d3ee' }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Schedule Dialog */}
      <Dialog open={openGenerateScheduleDialog} onClose={() => setOpenGenerateScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#22d3ee' }}>Generate Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            This still generates immediately. To run overnight or on a repeating day without logging in, use Automatic advertising schedule under Settings.
          </Typography>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={generateScheduleForm.station_id}
                    onChange={(e) => setGenerateScheduleForm({ ...generateScheduleForm, station_id: e.target.value })}
                    label="Station"
                  >
                    {stations.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={generateScheduleForm.date_from}
                  onChange={(e) => setGenerateScheduleForm({ ...generateScheduleForm, date_from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={generateScheduleForm.date_to}
                  onChange={(e) => setGenerateScheduleForm({ ...generateScheduleForm, date_to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        {scheduleGenerating && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'var(--rs-muted)' }}>
                {scheduleProgress.message || 'Generating...'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
                {scheduleProgress.percent}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={scheduleProgress.percent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'var(--rs-border)',
                '& .MuiLinearProgress-bar': { bgcolor: '#22c55e', borderRadius: 4 },
              }}
            />
          </Box>
        )}
        <DialogActions>
          <Button onClick={() => setOpenGenerateScheduleDialog(false)} disabled={scheduleGenerating}>Cancel</Button>
          <Button
            variant="contained"
            disabled={scheduleGenerating}
            onClick={async () => {
              if (!generateScheduleForm.station_id) {
                alert('Please select a station');
                return;
              }
              if (!generateScheduleForm.date_from) {
                alert('Please select a start date');
                return;
              }
              if (!generateScheduleForm.date_to) {
                alert('Please select an end date');
                return;
              }
              if (new Date(generateScheduleForm.date_from) > new Date(generateScheduleForm.date_to)) {
                alert('Start date must be before or equal to end date');
                return;
              }
              setScheduleGenerating(true);
              scheduleGenerationPendingRef.current = true;
              setScheduleProgress({ percent: 0, message: 'Starting generation...' });
              try {
                const body = {
                  station_id: generateScheduleForm.station_id,
                  date_from: generateScheduleForm.date_from,
                  date_to: generateScheduleForm.date_to,
                  async: true,
                };
                const res = await axios.post(`${API_URL}/api/advertising/generate-schedule`, body, { withCredentials: true });
                scheduleGenerationPendingRef.current = false;
                setScheduleGenerating(false);
                setOpenGenerateScheduleDialog(false);
                const queuePos = res?.data?.queue_position;
                alert(
                  queuePos
                    ? `Advertising schedule queued (position #${queuePos}). You can continue using the app while it runs in the background.`
                    : 'Advertising schedule queued. You can continue using the app while it runs in the background.'
                );
              } catch (e) {
                scheduleGenerationPendingRef.current = false;
                setScheduleGenerating(false);
                alert('Failed: ' + (e.response?.data?.message || e.message));
              }
            }}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
          >
            {scheduleGenerating ? `Generating... ${scheduleProgress.percent}%` : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to All Breaks Dialog */}
      <Dialog open={openAddToAllBreaksDialog} onClose={() => setOpenAddToAllBreaksDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Spot to All Breaks</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a spot to every commercial break for the selected date.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Station *</InputLabel>
                <Select
                  value={addToAllBreaksForm.station_id}
                  onChange={(e) => setAddToAllBreaksForm({ ...addToAllBreaksForm, station_id: e.target.value })}
                  label="Station *"
                >
                  {stations.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Campaign *</InputLabel>
                <Select
                  value={addToAllBreaksForm.campaign_id}
                  onChange={async (e) => {
                    const cid = e.target.value;
                    setAddToAllBreaksForm({ ...addToAllBreaksForm, campaign_id: cid, spot_id: '' });
                    setAddToAllBreaksSpots([]);
                    if (cid) {
                      try {
                        const res = await axios.get(`${API_URL}/api/advertising/campaigns/${cid}/spots`, { withCredentials: true });
                        setAddToAllBreaksSpots(res.data || []);
                      } catch (er) {}
                    }
                  }}
                  label="Campaign *"
                >
                  <MenuItem value="">—</MenuItem>
                  {campaigns.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {addToAllBreaksForm.campaign_id && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Spot *</InputLabel>
                  <Select
                    value={addToAllBreaksForm.spot_id}
                    onChange={(e) => setAddToAllBreaksForm({ ...addToAllBreaksForm, spot_id: e.target.value })}
                    label="Spot *"
                  >
                    <MenuItem value="">—</MenuItem>
                    {addToAllBreaksSpots.filter((s) => !s.is_placeholder).map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name} ({s.duration}s)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Date *"
                type="date"
                value={addToAllBreaksForm.date}
                onChange={(e) => setAddToAllBreaksForm({ ...addToAllBreaksForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddToAllBreaksDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const { station_id, campaign_id, spot_id, date } = addToAllBreaksForm;
              if (!station_id || !campaign_id || !spot_id || !date) {
                alert('Station, campaign, spot and date required');
                return;
              }
              try {
                const res = await axios.post(
                  `${API_URL}/api/advertising/add-spot-to-all-breaks`,
                  { station_id, campaign_id, spot_id, date },
                  { withCredentials: true }
                );
                alert(`Added spot to ${res.data?.count || 0} break(s)`);
                setOpenAddToAllBreaksDialog(false);
                fetchTrafficLog();
              } catch (e) {
                alert('Failed: ' + (e.response?.data?.message || e.message));
              }
            }}
            sx={{ bgcolor: '#a855f7' }}
          >
            Add to All Breaks
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={bulkBreakToast.open}
        autoHideDuration={4000}
        onClose={() => setBulkBreakToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setBulkBreakToast((prev) => ({ ...prev, open: false }))}
          severity={bulkBreakToast.severity}
          sx={{ width: '100%' }}
        >
          {bulkBreakToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
